// /all-trips state for a cold back from a trip page.
//
// Inside this app a back from a trip page finds the list as it was: its
// scroll, "Load more" page, search results and row offsets live in memory
// (hooks/useScrollPreservation.js, GlobalStates.js). A trip page served by the
// Astro app is another document. Going back to this page then restores it from
// the browser's back-forward cache when the browser kept it (nothing to do),
// or boots this app again from scratch. For that second case the state is
// saved to sessionStorage on pagehide (and just before a hand-off) and read
// once at boot, only when this document was opened by back/forward on the same
// URL. Normal loads and reloads ignore it.
//
// Parts of the state live in modules that import this one, so each registers
// a provider instead of being imported here.

const KEY = "all-trips-list-state";
const VERSION = 1;
// Search results are the only large part. Above this size they are left out:
// the list refetches as on a fresh load, the scroll positions still return.
const MAX_CHARS = 1500000;

const providers = [];
export const registerListStateProvider = (provide) => {
  providers.push(provide);
};

const currentUrl = () => window.location.pathname + window.location.search;
const TRIP_PATH = /^\/(trips|trip|t)\//;

// `url`: the list URL the state belongs to (TripRoute saves while the router
// already shows the trip URL); this page's URL by default.
export const saveListState = (url = currentUrl()) => {
  try {
    const state = Object.assign({ v: VERSION, url }, ...providers.map((provide) => provide()));
    let json = JSON.stringify(state);
    if (json.length > MAX_CHARS) json = JSON.stringify({ ...state, searchedTrips: undefined });
    window.sessionStorage.setItem(KEY, json);
  } catch {
    // Storage blocked or full: a cold back starts fresh, as before.
  }
};

const navigationType = () => {
  try {
    return performance.getEntriesByType("navigation")[0]?.type ?? null;
  } catch {
    return null;
  }
};

// What was saved for this URL when this document was opened by back/forward;
// null for any other load.
export const bootListState = (() => {
  if (typeof window === "undefined" || navigationType() !== "back_forward") return null;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(KEY) || "null");
    return saved && saved.v === VERSION && saved.url === currentUrl() ? saved : null;
  } catch {
    return null;
  }
})();

// Home's first mount after such a boot: the search page name whose results
// were restored (so it keeps them instead of refetching), else null. Once.
let restoredResultsPending = Boolean(bootListState?.searchedTrips?.length);
export const takeRestoredResults = (searchPageName) => {
  if (!restoredResultsPending) return null;
  restoredResultsPending = false;
  return bootListState.url === currentUrl() ? searchPageName : null;
};

// Leaving from a trip URL (TripRoute's hand-off) must not overwrite the list's state.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    if (!TRIP_PATH.test(window.location.pathname)) saveListState();
  });
}
